import { useEffect, useState } from "react";
import {
  fetchModuleGlobalConfig,
  type ModuleGlobalConfig,
} from "../services/moduleGlobalConfigService";

export interface UseModuleGlobalConfigResult {
  config: ModuleGlobalConfig | null;
  loading: boolean;
  error: string | null;
}

let cachedConfig: ModuleGlobalConfig | null = null;
let inFlight: Promise<ModuleGlobalConfig | null> | null = null;

async function loadConfigOnce(): Promise<ModuleGlobalConfig | null> {
  if (cachedConfig) return cachedConfig;
  if (inFlight !== null) return inFlight;

  inFlight = fetchModuleGlobalConfig()
    .then((res) => {
      cachedConfig = res?.data ?? null;
      return cachedConfig;
    })
    .catch((err: unknown) => {
      cachedConfig = null;
      const msg =
        err instanceof Error ? err.message : "Failed to load module config";
      throw new Error(msg);
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Loads GET /global/config once (deduped). Separate from admin-flags
 * {@link useGlobalConfig}.
 */
export function useModuleGlobalConfig(): UseModuleGlobalConfigResult {
  const [config, setConfig] = useState<ModuleGlobalConfig | null>(cachedConfig);
  const [loading, setLoading] = useState<boolean>(!cachedConfig);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    if (cachedConfig) {
      setConfig(cachedConfig);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);
    loadConfigOnce()
      .then((c) => {
        if (!mounted) return;
        setConfig(c);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load module config");
        setConfig(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { config, loading, error };
}

export function __resetModuleGlobalConfigCacheForTests(): void {
  cachedConfig = null;
  inFlight = null;
}
